import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CalendarCheck2, Loader2, ExternalLink } from "lucide-react";

const fmtD = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

export const PendingDaysBanner = ({ days, propertyId, locked, canApprove, onApproved }) => {
  const [busy, setBusy] = useState(null);
  if (!days || days.length === 0) return null;
  const approve = async (date) => {
    setBusy(date);
    try {
      await api.post("/payroll-sheet/sync-timeclock", { property_id: propertyId, start: date, end: date, overwrite: false });
      toast.success(`${fmtD(date)} approved from the Time Clock`);
      onApproved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not approve");
    } finally { setBusy(null); }
  };
  return (
    <div className="border border-sky-400/60 bg-sky-50 p-4 dark:bg-sky-950/20" data-testid="ps-pending-days">
      <div className="mb-1 flex items-center gap-2 text-sky-800 dark:text-sky-300">
        <CalendarCheck2 className="h-4 w-4" />
        <span className="text-sm font-bold">{days.length} day{days.length > 1 ? "s" : ""} not yet approved by GM</span>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Employees clocked in on these days, but the Daily Report payroll hasn't been saved, so their hours aren't counted yet (today is never counted). Open the Daily Report to check hours and enter housekeeper rooms, or approve the clocked hours here.
      </p>
      <div className="space-y-1">
        {days.map((d) => (
          <div key={d.date} className="flex flex-wrap items-center justify-between gap-2 rounded-sm border border-sky-400/40 bg-white px-3 py-1.5 text-sm dark:bg-transparent" data-testid={`ps-pending-${d.date}`}>
            <span className="font-medium">{fmtD(d.date)}</span>
            <span className="flex flex-wrap items-center gap-3 text-xs">
              <span className="font-mono text-muted-foreground">{d.employees} employee{d.employees > 1 ? "s" : ""} · <b className="tabular-nums text-foreground">{d.hours.toFixed(2)}</b> clocked hrs</span>
              <Link to={`/daily-reports/${propertyId}/${d.date}`} className="inline-flex items-center gap-1 font-semibold text-primary hover:underline" data-testid={`ps-pending-open-${d.date}`}>
                Open Daily Report <ExternalLink className="h-3 w-3" />
              </Link>
              {canApprove && !locked && (
                <button type="button" onClick={() => approve(d.date)} disabled={!!busy}
                  className="inline-flex items-center gap-1 rounded-sm bg-primary px-2.5 py-1 font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
                  data-testid={`ps-pending-approve-${d.date}`}>
                  {busy === d.date && <Loader2 className="h-3 w-3 animate-spin" />} Approve clocked hours
                </button>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
